import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  Table,
  Checkbox,
  Input,
  message,
  Tag,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { tenantApi, Tenant } from '../services/api/tenant';
import { mappingApi } from '../services/api/mapping';

const { Title, Text } = Typography;

interface FieldConfig {
  fieldName: string;
  displayName: string;
  dataType: string;
  selected: boolean;
  alias?: string;
  mandatory?: boolean;
  isColumn?: boolean;
}

export function TenantDocTypeConfig() {
  const { tenantId, docType } = useParams<{
    tenantId: string;
    docType: string;
  }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [docTypeData, setDocTypeData] = useState<any>(null);
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId && docType) {
      fetchData();
    }
  }, [tenantId, docType]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tenantResponse, docTypeResponse] = await Promise.all([
        tenantApi.get(tenantId!),
        mappingApi.getDocType(docType!),
      ]);

      setTenant(tenantResponse.data);
      setDocTypeData(docTypeResponse.data);

      // Try to load existing configuration
      let existingConfig: any = null;
      try {
        const configResponse = await tenantApi.getDocTypeConfig(
          tenantId!,
          docType!
        );
        existingConfig = configResponse.data;
      } catch (err) {
        // No existing config - that's fine
      }

      // Build field list from DocType definition
      const fieldList: FieldConfig[] = [];
      const existingFieldMap = new Map(
        existingConfig?.fields?.map((f: any) => [f.fieldName, f]) || []
      );

      docTypeResponse.data.dataFields.forEach((field: any) => {
        if (field.dataType?.name === 'TABLE') {
          // Add table columns
          field.columns?.forEach((col: any) => {
            const existing = existingFieldMap.get(col.fieldName);
            fieldList.push({
              fieldName: col.fieldName,
              displayName: col.displayName || col.fieldName,
              dataType: col.dataType?.name || 'STRING',
              selected: !!existing,
              alias: existing?.alias,
              mandatory: existing?.mandatory,
              isColumn: true,
            });
          });
        } else {
          // Add regular field
          const existing = existingFieldMap.get(field.fieldName);
          fieldList.push({
            fieldName: field.fieldName,
            displayName: field.displayName || field.fieldName,
            dataType: field.dataType?.name || 'STRING',
            selected: !!existing,
            alias: existing?.alias,
            mandatory: existing?.mandatory,
            isColumn: false,
          });
        }
      });

      setFields(fieldList);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const selectedFields = fields
        .filter((f) => f.selected)
        .map((f) => ({
          fieldName: f.fieldName,
          alias: f.alias || undefined,
          mandatory: f.mandatory,
        }));

      if (selectedFields.length === 0) {
        message.warning('Please select at least one field');
        setSaving(false);
        return;
      }

      await tenantApi.saveDocTypeConfig(tenantId!, docType!, {
        fields: selectedFields,
      });

      message.success('Configuration saved successfully');
      navigate(`/tenants/${tenantId}/doctypes`);
    } catch (error: any) {
      console.error('Failed to save configuration:', error);
      message.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (
    index: number,
    updates: Partial<FieldConfig>
  ) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    setFields(newFields);
  };

  const columns = [
    {
      title: 'Select',
      key: 'select',
      width: 80,
      render: (_: any, record: FieldConfig, index: number) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            updateField(index, { selected: e.target.checked })
          }
        />
      ),
    },
    {
      title: 'Field Name',
      dataIndex: 'fieldName',
      key: 'fieldName',
      render: (text: string, record: FieldConfig) => (
        <Space>
          {text}
          {record.isColumn && <Tag color="blue">Line Item</Tag>}
        </Space>
      ),
    },
    {
      title: 'Original Display Name',
      dataIndex: 'displayName',
      key: 'displayName',
    },
    {
      title: 'Data Type',
      dataIndex: 'dataType',
      key: 'dataType',
      render: (type: string) => <Tag>{type}</Tag>,
    },
    {
      title: 'Custom Alias',
      key: 'alias',
      render: (_: any, record: FieldConfig, index: number) => (
        <Input
          size="small"
          placeholder="Custom label..."
          value={record.alias}
          disabled={!record.selected}
          onChange={(e) =>
            updateField(index, { alias: e.target.value })
          }
        />
      ),
    },
    {
      title: 'Mandatory',
      key: 'mandatory',
      width: 100,
      render: (_: any, record: FieldConfig, index: number) => (
        <Checkbox
          checked={record.mandatory}
          disabled={!record.selected}
          onChange={(e) =>
            updateField(index, { mandatory: e.target.checked })
          }
        />
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !tenant || !docTypeData) {
    return (
      <div style={{ padding: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/tenants/${tenantId}/doctypes`)}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
        <Alert
          message="Error"
          description={error || 'Failed to load data'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/tenants/${tenantId}/doctypes`)}
              style={{ marginBottom: 16 }}
            >
              Back
            </Button>
            <Title level={2}>
              {tenant.name} - {docTypeData.name} Configuration
            </Title>
            <Text type="secondary">
              Select fields to show for this tenant and optionally customize
              their labels and requirements.
            </Text>
          </div>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            size="large"
          >
            Save Configuration
          </Button>
        </div>

        <Card>
          <Table
            columns={columns}
            dataSource={fields}
            rowKey="fieldName"
            pagination={false}
            size="small"
          />
        </Card>

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Text type="secondary">
              {fields.filter((f) => f.selected).length} of {fields.length}{' '}
              fields selected
            </Text>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              Save Configuration
            </Button>
          </Space>
        </div>
      </Space>
    </div>
  );
}